import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientFrame, PersistedEvent, ServerFrame, StreamDelta } from './types';

export interface FormingTool {
  tool_name: string;
  partial_json: string;
}

export interface SessionSocket {
  events: PersistedEvent[];
  streamText: string;
  formingTools: Record<string, FormingTool>;
  status: string;
  connected: boolean;
  sendTurn: (text: string) => void;
  interrupt: () => void;
}

/**
 * Live session WebSocket (docs/events.ts protocol). Endpoint /api/sessions/{id}/ws,
 * cookie-authenticated at handshake. Replays persisted events on connect, then applies
 * live event + delta frames; reconnects with backoff and replays from the last seq.
 */
export function useSessionSocket(sessionId: string): SessionSocket {
  const [events, setEvents] = useState<PersistedEvent[]>([]);
  const [streamText, setStreamText] = useState('');
  const [formingTools, setFormingTools] = useState<Record<string, FormingTool>>({});
  const [status, setStatus] = useState('');
  const [connected, setConnected] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const lastSeqRef = useRef(0);
  const backoffRef = useRef(1000);
  const closedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const helloTimerRef = useRef<number | null>(null);

  const applyDelta = useCallback((d: StreamDelta) => {
    if (d.kind === 'text_delta') {
      setStreamText((s) => s + d.text);
    } else if (d.kind === 'tool_input_start') {
      setFormingTools((t) => ({ ...t, [d.tool_use_id]: { tool_name: d.tool_name, partial_json: '' } }));
    } else if (d.kind === 'tool_input_delta') {
      setFormingTools((t) =>
        t[d.tool_use_id]
          ? { ...t, [d.tool_use_id]: { ...t[d.tool_use_id], partial_json: t[d.tool_use_id].partial_json + d.partial_json } }
          : t,
      );
    }
  }, []);

  const handle = useCallback(
    (f: ServerFrame) => {
      if (f.t === 'replay') {
        // Il replay dopo una riconnessione porta solo gli eventi con seq > after_seq: va FUSO con
        // quello che già abbiamo, non sostituito (sostituire azzerava la chat a ogni riconnessione,
        // cioè a ogni blocco/sblocco dell'iPhone). Dedup su seq per sicurezza.
        setEvents((p) => {
          const seen = new Set(p.map((e) => e.seq));
          const merged = [...p, ...f.events.filter((e) => !seen.has(e.seq))];
          merged.sort((a, b) => a.seq - b.seq);
          return merged;
        });
        lastSeqRef.current = Math.max(lastSeqRef.current, f.last_seq);
        if (helloTimerRef.current !== null) {
          window.clearTimeout(helloTimerRef.current);
          helloTimerRef.current = null;
        }
      } else if (f.t === 'event') {
        setEvents((p) => [...p, f.event]);
        lastSeqRef.current = f.event.seq;
        // A coalesced assistant_text supersedes the live tail; a result ends the turn.
        if (f.event.type === 'assistant_text' || f.event.type === 'result') setStreamText('');
        if (f.event.type === 'result') setFormingTools({});
      } else if (f.t === 'delta') {
        applyDelta(f.delta);
      } else if (f.t === 'status') {
        setStatus(f.status);
      }
    },
    [applyDelta],
  );

  const connect = useCallback(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${window.location.host}/api/sessions/${sessionId}/ws`);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      backoffRef.current = 1000;
      ws.send(JSON.stringify({ t: 'hello', after_seq: lastSeqRef.current } satisfies ClientFrame));
      // Se il replay non arriva entro pochi secondi (hello perso, proxy anomalo), non restare
      // muti per sempre: chiudi e lascia fare alla riconnessione con backoff.
      helloTimerRef.current = window.setTimeout(() => {
        helloTimerRef.current = null;
        ws.close();
      }, 4000);
    };
    ws.onmessage = (ev) => {
      try {
        handle(JSON.parse(ev.data as string) as ServerFrame);
      } catch {
        /* ignore malformed frame */
      }
    };
    ws.onclose = () => {
      setConnected(false);
      if (helloTimerRef.current !== null) {
        window.clearTimeout(helloTimerRef.current);
        helloTimerRef.current = null;
      }
      if (closedRef.current) return;
      reconnectTimerRef.current = window.setTimeout(connect, backoffRef.current);
      backoffRef.current = Math.min(backoffRef.current * 2, 15000);
    };
    ws.onerror = () => ws.close();
  }, [sessionId, handle]);

  useEffect(() => {
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true;
      if (reconnectTimerRef.current !== null) window.clearTimeout(reconnectTimerRef.current);
      if (helloTimerRef.current !== null) window.clearTimeout(helloTimerRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const sendTurn = useCallback((text: string) => {
    wsRef.current?.send(JSON.stringify({ t: 'turn', text } satisfies ClientFrame));
  }, []);
  const interrupt = useCallback(() => {
    wsRef.current?.send(JSON.stringify({ t: 'interrupt' } satisfies ClientFrame));
  }, []);

  return { events, streamText, formingTools, status, connected, sendTurn, interrupt };
}
